import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { UserCheck, Clock, ChevronRight, ChevronLeft, Search, User, Settings, Fingerprint, Check, AlertCircle, Loader, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import apiService from '../services/api';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import shiftApi from '../services/shiftApi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import ActiveAttendanceList from '../components/ActiveAttendanceList';

interface MachineGroup {
  id: number;
  name: string;
}

interface Machine {
  id: number;
  machineNumber: number;
  name?: string;
  groupId?: number;
}

interface Employee {
  id: number;
  userId: number;
  hasFingerprint?: boolean;
  fingerprintTemplate?: string | null;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

type StepperStep = 'employee' | 'machines' | 'fingerprint' | 'verifying' | 'success' | 'error';
type ViewMode = 'mark' | 'list';

const Attendance: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isConnected } = useWebSocket();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('mark');
  const [currentStep, setCurrentStep] = useState<StepperStep>('employee');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState<number[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Helper function to check if user has admin-level attendance permissions
  const hasAdminAttendanceAccess = (): boolean => {
    if (!user) return false;
    
    if (user.role === 'SUPERADMIN' || user.role === 'ADMIN') {
      return true;
    }
    
    const hasEmployeesModule = !!(user.userRole?.permissions?.some((p: any) => 
      p.module?.name === 'employees' && p.canView
    ) || user.permissions?.some((p: any) => 
      p.module?.name === 'employees' && p.canView
    ));
    
    const hasFingerprintModule = !!(user.userRole?.permissions?.some((p: any) => 
      p.module?.name === 'fingerprint-management' && p.canView
    ) || user.permissions?.some((p: any) => 
      p.module?.name === 'fingerprint-management' && p.canView
    ));
    
    return hasEmployeesModule && hasFingerprintModule;
  };

  const canMarkAttendanceForOthers = hasAdminAttendanceAccess();

  // Fetch machine groups
  const { data: groupsResponse, isLoading: loadingGroups } = useQuery({
    queryKey: ['machine-groups'],
    queryFn: () => apiService.get('/machine-groups'),
  });

  const machineGroups: MachineGroup[] = groupsResponse?.groups || [];

  // Fetch machines
  const { data: machinesResponse, isLoading: loadingMachines } = useQuery({
    queryKey: ['machines'],
    queryFn: () => apiService.get('/machines'),
    enabled: viewMode === 'mark'
  });

  const allMachines: Machine[] = machinesResponse?.machines || [];
  const ungroupedMachines = allMachines.filter(m => !m.groupId);

  // Fetch employees
  const { data: employeesResponse, isLoading: loadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
    enabled: viewMode === 'mark'
  });

  const employees: Employee[] = employeesResponse?.employees || [];

  // Filter employees based on search query
  const filteredEmployees = employees.filter(employee => {
    const query = searchQuery.toLowerCase();
    return (
      employee.user.name.toLowerCase().includes(query) ||
      employee.user.email.toLowerCase().includes(query)
    );
  });

  // Auto-select current employee if cannot mark for others
  useEffect(() => {
    if (!canMarkAttendanceForOthers && user?.employee?.id && viewMode === 'mark') {
      setSelectedEmployeeId(user.employee.id);
    }
  }, [canMarkAttendanceForOthers, user, viewMode]);

  // Fetch selected employee's fingerprint data
  const { data: fingerprintData, isLoading: loadingFingerprint } = useQuery({
    queryKey: ['fingerprint-template', selectedEmployeeId],
    queryFn: () => fingerprintApi.getFingerprintTemplate(selectedEmployeeId!),
    enabled: !!selectedEmployeeId && currentStep === 'fingerprint' && viewMode === 'mark'
  });

  // Get available devices
  const { data: devicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: viewMode === 'mark'
  });

  const devices: FingerprintDevice[] = devicesData?.devices || [];

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId && currentStep === 'fingerprint' && viewMode === 'mark') {
      const firstActiveDevice = devices.find(d => d.status === 'active') || devices[0];
      if (firstActiveDevice) {
        setSelectedDeviceId(firstActiveDevice.deviceId);
      }
    }
  }, [devices, selectedDeviceId, currentStep, viewMode]);

  // Mark attendance mutation
  const markAttendanceMutation = useMutation({
    mutationFn: (data: {
      employeeId: number;
      machineGroupIds?: number[];
      machineIds?: number[];
      fingerprintTemplate: string;
      deviceId: number;
    }) => shiftApi.markAttendanceWithFingerprint(data),
    onSuccess: () => {
      setCurrentStep('success');
      toast.success('Attendance marked successfully');
      queryClient.invalidateQueries({ queryKey: ['active-attendances'] });
      setTimeout(() => {
        resetForm();
        setViewMode('list');
      }, 2000);
    },
    onError: (error: any) => {
      setCurrentStep('error');
      setErrorMessage(error.response?.data?.message || 'Failed to mark attendance');
      toast.error(error.response?.data?.message || 'Failed to mark attendance');
    }
  });

  const resetForm = () => {
    setCurrentStep('employee');
    setSelectedGroupIds([]);
    setSelectedMachineIds([]);
    setSelectedEmployeeId(null);
    setSelectedDeviceId(null);
    setSearchQuery('');
    setErrorMessage('');
    setStatusMessage('');
  };

  const toggleGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const toggleMachine = (machineId: number) => {
    setSelectedMachineIds(prev =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const handleNext = () => {
    if (currentStep === 'employee') {
      if (!selectedEmployeeId) {
        toast.error('Please select an employee');
        return;
      }
      setCurrentStep('machines');
    } else if (currentStep === 'machines') {
      if (selectedGroupIds.length === 0 && selectedMachineIds.length === 0) {
        toast.error('Please select at least one machine group or machine');
        return;
      }
      setCurrentStep('fingerprint');
    }
  };

  const handleBack = () => {
    if (currentStep === 'machines') {
      setCurrentStep('employee');
    } else if (currentStep === 'fingerprint') {
      setCurrentStep('machines');
    }
  };

  const handleDone = async () => {
    if (!selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }

    if (selectedGroupIds.length === 0 && selectedMachineIds.length === 0) {
      toast.error('Please select at least one machine group or machine');
      return;
    }

    if (!selectedDeviceId) {
      toast.error('Please select a fingerprint device');
      return;
    }

    if (!isConnected) {
      toast.error('WebSocket not connected. Please wait...');
      return;
    }

    if (!fingerprintData?.employee?.hasFingerprint || !fingerprintData?.employee?.fingerprintTemplate) {
      toast.error('Employee does not have fingerprint enrolled');
      navigate('/employees');
      return;
    }

    const fingerprintTemplate = fingerprintData.employee.fingerprintTemplate;

    try {
      setCurrentStep('verifying');
      setErrorMessage('');
      setStatusMessage('Verifying fingerprint...');

      await fingerprintApi.verifyFingerprint({
        deviceId: selectedDeviceId,
        fingerprintTemplate: fingerprintTemplate
      });

      setStatusMessage('Fingerprint verified. Marking attendance...');

      markAttendanceMutation.mutate({
        employeeId: selectedEmployeeId,
        machineGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : [],
        machineIds: selectedMachineIds.length > 0 ? selectedMachineIds : [],
        fingerprintTemplate: fingerprintTemplate,
        deviceId: selectedDeviceId
      });
    } catch (error: any) {
      setCurrentStep('error');
      setErrorMessage(error.response?.data?.message || 'Fingerprint verification failed');
      toast.error(error.response?.data?.message || 'Fingerprint verification failed');
    }
  };

  const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
  const hasFingerprint = fingerprintData?.employee?.hasFingerprint || selectedEmployee?.hasFingerprint;
  const isLoading = loadingGroups || loadingEmployees || loadingMachines;

  const steps = [
    { id: 'employee', label: 'Select Employee', icon: User },
    { id: 'machines', label: 'Select Machines', icon: Settings },
    { id: 'fingerprint', label: 'Fingerprint', icon: Fingerprint },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* Mark Attendance Flow */}
      {viewMode === 'mark' && (
        <div className="flex-1 overflow-hidden bg-white flex flex-col">
              {/* Tabs and Stepper Combined */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                {/* Tabs */}
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setViewMode('mark');
                      resetForm();
                    }}
                    className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 bg-primary-600 text-white shadow-sm"
                  >
                    <Fingerprint className="w-4 h-4" />
                    Mark Attendance
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 text-gray-600 hover:bg-gray-200"
                  >
                    <Clock className="w-4 h-4" />
                    Active List
                  </button>
                </div>
                
                {/* Stepper */}
                <div className="flex items-center gap-3">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isActive = currentStepIndex === index;
                    const isCompleted = currentStepIndex > index;

                    return (
                      <React.Fragment key={step.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${
                              isActive
                                ? 'bg-primary-600 border-primary-600 text-white'
                                : isCompleted
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'bg-white border-gray-300 text-gray-400'
                            }`}
                          >
                            <StepIcon className="w-4 h-4" />
                          </div>
                          <span className={`text-xs font-semibold ${isActive ? 'text-primary-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                            {step.label}
                          </span>
                        </div>
                        {index < steps.length - 1 && (
                          <div className={`w-8 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Step Content - Scrollable with Fixed Buttons */}
              <div className="flex-1 min-h-0 flex flex-col p-4">
              {/* Step 1: Select Employee */}
              {currentStep === 'employee' && (
                <div className="flex-1 min-h-0 flex flex-col w-full">
                  <div className="mb-2 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-900">Select Employee</h2>
                  </div>
                  
                  {/* Search Bar */}
                  <div className="relative mb-2 flex-shrink-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search employee by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Employee List - Scrollable */}
                  <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-y-auto bg-white">
                      {isLoading ? (
                        <div className="p-8 text-center">
                          <Loader className="w-6 h-6 animate-spin text-primary-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Loading employees...</p>
                        </div>
                      ) : filteredEmployees.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No employees found</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {filteredEmployees.map((employee) => {
                            const isSelected = selectedEmployeeId === employee.id;
                            return (
                              <button
                                key={employee.id}
                                onClick={() => setSelectedEmployeeId(employee.id)}
                                disabled={!canMarkAttendanceForOthers && user?.employee?.id !== employee.id}
                                className={`w-full p-3 text-left hover:bg-gray-50 transition-all ${
                                  isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                                } ${!canMarkAttendanceForOthers && user?.employee?.id !== employee.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <User className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                      <div className="text-base font-semibold text-gray-900">{employee.user.name}</div>
                                      <div className="text-xs text-gray-600">{employee.user.email}</div>
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {!canMarkAttendanceForOthers && user?.employee?.id && (
                      <p className="mt-2 text-xs text-gray-500 text-center flex-shrink-0">
                        You can only mark attendance for yourself
                      </p>
                    )}

                  <div className="flex justify-end gap-3 pt-2 mt-2 border-t flex-shrink-0">
                    <button
                      onClick={handleNext}
                      disabled={!selectedEmployeeId || isLoading}
                      className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
                    >
                      Next
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Select Machines */}
              {currentStep === 'machines' && (
                <div className="flex-1 min-h-0 flex flex-col w-full">
                  <div className="mb-2 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-900">Select Machine Groups (Lines) or Machines</h2>
                  </div>
                  
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
                    {/* Machine Groups Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Machine Groups (Lines)</h3>
                      {machineGroups.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">No machine groups available.</p>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {machineGroups.map((group) => {
                            const isSelected = selectedGroupIds.includes(group.id);
                            return (
                              <button
                                key={group.id}
                                onClick={() => toggleGroup(group.id)}
                                className={`p-3 border-2 rounded-lg text-center hover:bg-gray-50 transition-all ${
                                  isSelected
                                    ? 'border-primary-600 bg-primary-50 shadow-sm'
                                    : 'border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-center gap-2">
                                  <span className="font-semibold text-gray-900 text-sm">{group.name}</span>
                                  {isSelected && (
                                    <Check className="w-4 h-4 text-primary-600" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Individual Machines Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Individual Machines</h3>
                      {ungroupedMachines.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">No ungrouped machines available.</p>
                      ) : (
                        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                          {ungroupedMachines.map((machine) => {
                            const isSelected = selectedMachineIds.includes(machine.id);
                            return (
                              <button
                                key={machine.id}
                                onClick={() => toggleMachine(machine.id)}
                                className={`p-2.5 border-2 rounded-lg text-center hover:bg-gray-50 transition-all ${
                                  isSelected
                                    ? 'border-primary-600 bg-primary-50 shadow-sm'
                                    : 'border-gray-300'
                                }`}
                              >
                                <div className="flex flex-col items-center justify-center">
                                  <span className="font-semibold text-gray-900 text-xs">M{machine.machineNumber}</span>
                                  {isSelected && (
                                    <Check className="w-3 h-3 text-primary-600 mt-1" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 pt-2 mt-2 border-t flex-shrink-0">
                    <button
                      onClick={handleBack}
                      className="px-6 py-2.5 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold flex items-center gap-2"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={selectedGroupIds.length === 0 && selectedMachineIds.length === 0}
                      className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
                    >
                      Next
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Fingerprint */}
              {currentStep === 'fingerprint' && (
                <div className="flex-1 min-h-0 flex flex-col w-full">
                  <div className="mb-2 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-900">Enter Fingerprint</h2>
                  </div>
                  
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
                    {/* Employee Summary */}
                    {selectedEmployee && (
                      <div className="bg-primary-50 rounded-lg p-3 border border-primary-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-base font-bold text-gray-900">{selectedEmployee.user.name}</div>
                            <div className="text-xs text-gray-600">{selectedEmployee.user.email}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Selected Machines Summary */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Selected:</div>
                      <div className="space-y-1">
                        {selectedGroupIds.length > 0 && (
                          <div className="text-sm text-gray-700">
                            <span className="font-semibold">Groups:</span> {machineGroups.filter(g => selectedGroupIds.includes(g.id)).map(g => g.name).join(', ')}
                          </div>
                        )}
                        {selectedMachineIds.length > 0 && (
                          <div className="text-sm text-gray-700">
                            <span className="font-semibold">Machines:</span> {selectedMachineIds.map(id => {
                              const machine = allMachines.find(m => m.id === id);
                              return machine ? `M${machine.machineNumber}` : '';
                            }).filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fingerprint Status */}
                    {loadingFingerprint ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
                        <Loader className="w-4 h-4 animate-spin" />
                        Checking fingerprint status...
                      </div>
                    ) : hasFingerprint ? (
                      <div className="flex items-center gap-2 text-sm text-green-600 p-3 bg-green-50 rounded-lg border border-green-200">
                        <Fingerprint className="w-4 h-4" />
                        <span className="font-semibold">Fingerprint enrolled</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-red-600 p-3 bg-red-50 rounded-lg border border-red-200">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-semibold">Fingerprint not enrolled. Please enroll first.</span>
                      </div>
                    )}

                    {/* Device Selection - Hidden, auto-select first active device */}
                    {hasFingerprint && !isConnected && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-800 font-medium">⚠️ WebSocket not connected. Please wait...</p>
                      </div>
                    )}

                    {/* Fingerprint Icon Display */}
                    {hasFingerprint && selectedDeviceId && (
                      <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <Fingerprint className="w-20 h-20 text-primary-600 mb-3" />
                        <p className="text-lg font-bold text-gray-900 mb-1">Please place your finger on the sensor</p>
                        <p className="text-sm text-gray-600">Fingerprint will be verified when you click Done</p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between gap-3 pt-2 mt-2 border-t flex-shrink-0">
                    <button
                      onClick={handleBack}
                      className="px-6 py-2.5 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold flex items-center gap-2"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      onClick={handleDone}
                      disabled={
                        !hasFingerprint ||
                        !selectedDeviceId ||
                        !isConnected ||
                        markAttendanceMutation.isPending
                      }
                      className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
                    >
                      <Check className="w-5 h-5" />
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Verifying Step */}
              {currentStep === 'verifying' && (
                <div className="flex-1 flex items-center justify-center text-center">
                  <div>
                    <Loader className="w-16 h-16 mx-auto text-primary-600 mb-4 animate-spin" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Verifying Fingerprint</h3>
                    <p className="text-base text-gray-600 mb-2">
                      Please place your finger on the sensor and wait...
                    </p>
                    {statusMessage && (
                      <p className="text-base text-primary-600 font-semibold mt-2">
                        {statusMessage}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Success Step */}
              {currentStep === 'success' && (
                <div className="flex-1 flex items-center justify-center text-center">
                  <div>
                    <Check className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Attendance Marked Successfully</h3>
                    <p className="text-base text-gray-600">
                      The attendance has been recorded.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Step */}
              {currentStep === 'error' && (
                <div className="flex-1 flex items-center justify-center text-center">
                  <div>
                    <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Error</h3>
                    <p className="text-base text-red-600 mb-4">{errorMessage}</p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => {
                          setCurrentStep('fingerprint');
                          setErrorMessage('');
                        }}
                        className="px-6 py-2.5 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={resetForm}
                        className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
                      >
                        Start Over
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Active Attendances List */}
      {viewMode === 'list' && (
        <div className="flex-1 overflow-auto bg-white flex flex-col">
          {/* Tabs */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center">
            <div className="flex gap-1">
              <button
                onClick={() => {
                  setViewMode('mark');
                  resetForm();
                }}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 text-gray-600 hover:bg-gray-200"
              >
                <Fingerprint className="w-4 h-4" />
                Mark Attendance
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 bg-primary-600 text-white shadow-sm"
              >
                <Clock className="w-4 h-4" />
                Active List
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto">
            <ActiveAttendanceList canMarkAttendanceForOthers={canMarkAttendanceForOthers} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
